import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { sectionReveal, heroReveal, delayedReveal } from "@/lib/motion";
import { ShieldCheck, Mail, Lock, Database, Clock, UserCog, Baby, FileText, RefreshCw, ArrowLeft, Landmark } from "lucide-react";
import { Header } from "@/components/Header";
import { AuLawTable } from "@/components/AuLawTable";
import { Footer } from "@/components/Footer";
import { PrintButton } from "@/components/PrintButton";

const LAST_UPDATED = "June 20, 2026";
const CONTACT_EMAIL = "emergo.support@gmail.com";

const auPrivacyLaws = [
  ["New South Wales", "Privacy and Personal Information Protection Act 1998; Health Records and Information Privacy Act 2002"],
  ["Victoria", "Privacy and Data Protection Act 2014; Health Records Act 2001"],
  ["Queensland", "Information Privacy Act 2009"],
  ["Western Australia", "Privacy and Responsible Information Sharing Act 2024"],
  ["South Australia", "Information Privacy Principles (IPPS) Instruction"],
  ["Tasmania", "Personal Information Protection Act 2004"],
  ["Australian Capital Territory", "Information Privacy Act 2014; Health Records (Privacy and Access) Act 1997"],
  ["Northern Territory", "Information Act 2002"],
];

const tableOfContents = [
  { id: "intro", label: "Introduction" },
  { id: "information-we-collect", label: "Information We Collect" },
  { id: "how-we-use", label: "How We Use Your Information" },
  { id: "no-third-party-sharing", label: "No Third-Party Sharing" },
  { id: "storage-and-protection", label: "How We Store and Protect Your Data" },
  { id: "data-retention", label: "Data Retention" },
  { id: "your-rights", label: "Your Rights" },
  { id: "australia", label: "Australian Privacy Law" },
  { id: "childrens-privacy", label: "Children's Privacy" },
  { id: "changes", label: "Changes to This Policy" },
  { id: "contact", label: "Contact Us" },
];

const dataCategories = [
  { title: "Name and email", desc: "Used to create your account and to communicate with you about your use of Emergo." },
  { title: "Account login and password", desc: "Used to authenticate you and keep your account secure. Passwords are stored in a protected, non-readable form." },
  { title: "Location data", desc: "Used to provide location-aware features within the app when you choose to enable them." },
  { title: "Photos and file uploads", desc: "Used to store and display the content you choose to add to the app." },
  { title: "Payment information", desc: "Used to process transactions you make within Emergo. It is handled only within the app and is never sold or shared." },
  { title: "Insurance information", desc: "Used to deliver the features you request. It is handled only within the app and is never sold or shared." },
];

const Section = ({ id, icon: Icon, title, children }) => (
  <motion.section
    id={id}
    data-testid={`section-${id}`}
    {...sectionReveal}
    className="scroll-mt-24"
  >
    <div className="flex items-center gap-3 mb-4">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand">
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
      </span>
      <h2 className="font-extrabold tracking-tight text-2xl md:text-[1.75rem] leading-tight text-ink">{title}</h2>
    </div>
    <div className="pl-0 md:pl-12 space-y-4 text-ink-2 leading-relaxed text-base">
      {children}
    </div>
  </motion.section>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-ink antialiased" data-testid="privacy-policy-page">
      <Header />

      <main className="mx-auto max-w-3xl px-6">
        {/* Title block */}
        <motion.div
          {...heroReveal}
          className="pt-16 pb-12 border-b border-gray-200"
        >
          <Link to="/" data-testid="back-to-home-link" className="print:hidden inline-flex items-center gap-2 text-sm text-gray-500 hover:text-brand transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
            Back to home
          </Link>
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-brand mb-4">Legal</p>
          <h1 className="font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink" data-testid="page-title">
            Privacy Policy
          </h1>
          <p className="mt-6 text-lg text-ink-2 max-w-xl leading-relaxed">
            This policy explains what information Emergo collects, how it is used, and the choices you have.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <p className="text-sm text-gray-500" data-testid="last-updated">Last updated: {LAST_UPDATED}</p>
            <PrintButton />
          </div>
        </motion.div>

        {/* Table of contents */}
        <motion.nav
          aria-label="Table of contents"
          data-testid="table-of-contents"
          {...delayedReveal}
          className="mt-12 rounded-2xl border border-gray-200 bg-white p-6 sm:p-8"
        >
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-brand mb-5">On this page</p>
          <ol className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
            {tableOfContents.map((item, i) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  data-testid={`toc-link-${item.id}`}
                  className="group flex items-baseline gap-3 rounded-md py-2 text-ink-2 transition-colors hover:text-brand"
                >
                  <span className="font-extrabold tracking-tight text-sm text-gray-400 tabular-nums transition-colors group-hover:text-brand">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[15px] underline-offset-4 group-hover:underline decoration-gray-300">{item.label}</span>
                </a>
              </li>
            ))}
          </ol>
        </motion.nav>

        {/* Body */}
        <div className="py-14 space-y-14">
          <Section id="intro" icon={FileText} title="Introduction">
            <p>
              Emergo ("Emergo", "we", "us", or "our") is committed to protecting your privacy. This Privacy Policy
              describes how we collect, use, store, and protect the information you provide when you use the Emergo
              application, and the rights you have over that information. By using Emergo, you agree to the practices
              described in this policy.
            </p>
            <p>
              This policy applies worldwide to all users of Emergo. We keep the language plain so that anyone can
              understand how their data is handled.
            </p>
          </Section>

          <Section id="information-we-collect" icon={Database} title="Information We Collect">
            <p>We collect only the information needed to provide and improve the app. The categories of data we collect are:</p>
            <ul className="space-y-4 not-prose">
              {dataCategories.map((c) => (
                <li key={c.title} className="rounded-xl border border-gray-200 bg-white p-5" data-testid={`data-category-${c.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                  <p className="font-medium text-ink">{c.title}</p>
                  <p className="mt-1 text-ink-2 text-[15px] leading-relaxed">{c.desc}</p>
                </li>
              ))}
            </ul>
          </Section>

          <Section id="how-we-use" icon={UserCog} title="How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-5 space-y-2 marker:text-brand">
              <li>Create and manage your account and keep it secure.</li>
              <li>Provide, maintain, and improve the features of the app.</li>
              <li>Process payments and deliver the services you request.</li>
              <li>Respond to your questions and provide support.</li>
              <li>Keep the app safe and prevent misuse.</li>
            </ul>
            <p>We do not use your information for advertising, and we do not sell your personal data.</p>
          </Section>

          <Section id="no-third-party-sharing" icon={Lock} title="No Third-Party Sharing">
            <p>
              Emergo does not share your personal information with third-party services. We do not use external
              analytics providers, advertising networks, or outside data processors. Your information is used only
              within Emergo to deliver the features you use.
            </p>
            <p>
              We may disclose information only where required to do so by law, or to protect the rights, safety, and
              security of our users and the app.
            </p>
          </Section>

          <Section id="storage-and-protection" icon={ShieldCheck} title="How We Store and Protect Your Data">
            <p>
              We store your information securely and apply reasonable technical and organizational measures to protect
              it against unauthorized access, loss, or misuse. Passwords are stored in a protected, non-readable form,
              and access to personal data is limited to what is necessary to operate the app.
            </p>
            <p>
              While we work hard to safeguard your information, no method of transmission or storage is completely
              secure, and we cannot guarantee absolute security.
            </p>
          </Section>

          <Section id="data-retention" icon={Clock} title="Data Retention">
            <p>
              We keep your information for as long as your account is active or as needed to provide the app to you.
              When you ask us to delete your data, or when it is no longer needed for the purposes described in this
              policy, we remove it, unless we are required to keep it to comply with a legal obligation.
            </p>
          </Section>

          <Section id="your-rights" icon={UserCog} title="Your Rights">
            <p>You have control over your information. You may:</p>
            <ul className="list-disc pl-5 space-y-2 marker:text-brand">
              <li><span className="font-medium text-ink">Access</span> the personal data we hold about you.</li>
              <li><span className="font-medium text-ink">Correct</span> information that is inaccurate or out of date.</li>
              <li><span className="font-medium text-ink">Delete</span> your personal data and close your account.</li>
            </ul>
            <p>
              To exercise any of these rights, email us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-ink underline underline-offset-4 decoration-gray-300 hover:decoration-brand transition-colors" data-testid="rights-contact-link">{CONTACT_EMAIL}</a>.
              We will respond to your request within a reasonable time.
            </p>
          </Section>

          <Section id="australia" icon={Landmark} title="Australian Privacy Law">
            <p>
              Emergo (ABN: <span className="font-semibold text-ink" data-testid="privacy-abn">34 228 148 692</span>) handles
              personal information in line with the <em>Privacy Act 1988</em> (Cth) and the 13 Australian Privacy Principles
              (APPs). This applies to users in every Australian state and territory.
            </p>
            <ul className="list-disc pl-5 space-y-2 marker:text-brand">
              <li><span className="font-medium text-ink">Access and correction (APPs 12 and 13).</span> You can ask for a copy of, or a correction to, the personal information we hold about you. We will respond within 30 days and will not charge you for making a request.</li>
              <li><span className="font-medium text-ink">Anonymity (APP 2).</span> You can contact our support team without giving your real name, but we need an account to provide the app itself.</li>
              <li><span className="font-medium text-ink">Overseas storage (APP 8).</span> Your data may be stored with hosting and email providers located outside Australia, such as in the United States. Before using any provider, we take reasonable steps to make sure they protect your information to the standard the APPs require.</li>
              <li><span className="font-medium text-ink">Data breaches.</span> If a data breach is likely to cause you serious harm, we will notify you and the Office of the Australian Information Commissioner (OAIC) under the Notifiable Data Breaches scheme.</li>
              <li><span className="font-medium text-ink">Marketing (APP 7 and the <em>Spam Act 2003</em>).</span> We only send marketing emails with your consent, and every email includes a way to unsubscribe.</li>
              <li><span className="font-medium text-ink">No health information.</span> Emergo does not ask for or collect health, injury or medical information. Please do not enter any in notes or reports. If emergency services are needed, call 000.</li>
            </ul>
            <p className="font-medium text-ink">State and territory laws</p>
            <p>
              Most state and territory privacy laws cover government agencies, and private businesses like Emergo are covered
              by the federal Privacy Act. We follow any state or territory law that applies to us. Because we do not collect
              health information, the state health records laws are not expected to apply.
            </p>
            <AuLawTable testId="au-privacy-table" rows={auPrivacyLaws} />
            <p className="font-medium text-ink">Complaints</p>
            <p>
              If you think we have mishandled your personal information, email us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-ink underline underline-offset-4 decoration-gray-300 hover:decoration-brand transition-colors" data-testid="au-complaint-link">{CONTACT_EMAIL}</a>.
              We will respond within 30 days. If you are not satisfied with our response, you can complain to the OAIC at{" "}
              <a href="https://www.oaic.gov.au" target="_blank" rel="noreferrer" className="font-medium text-ink underline underline-offset-4 decoration-gray-300 hover:decoration-brand transition-colors" data-testid="oaic-link">oaic.gov.au</a>{" "}
              or on 1300 363 992.
            </p>
          </Section>

          <Section id="childrens-privacy" icon={Baby} title="Children's Privacy">
            <p>
              Emergo is not intended for children, and we do not knowingly collect personal information from children.
              If you believe a child has provided us with personal data, please contact us and we will take steps to
              remove that information.
            </p>
          </Section>

          <Section id="changes" icon={RefreshCw} title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we make changes, we will revise the "last
              updated" date at the top of this page. We encourage you to review this policy periodically to stay
              informed about how we protect your information.
            </p>
          </Section>

          <Section id="contact" icon={Mail} title="Contact Us">
            <p>
              If you have any questions about this Privacy Policy or would like to make a request regarding your data,
              please contact us at:
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="contact-email-link"
              className="inline-flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-4 text-ink transition-colors hover:border-brand group"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-soft text-brand transition-transform group-hover:scale-105">
                <Mail className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </span>
              <span className="font-medium">{CONTACT_EMAIL}</span>
            </a>
          </Section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
